import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { ApolloResponse } from '@/interfaces/apollo'
import { SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import ThirdPartyDialog from '@/modules/ThirdParty/scripts/third-party-dialog'
import { getThirdParties, editThirdParty } from '@/modules/ThirdParty/gql/third-party.gql'
import { ThirdParty } from '@/modules/ThirdParty/interfaces/third-party'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField
  }
})
export default class ThirdPartyList extends mixins(ThirdPartyDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Setting'
    },
    {
      text: 'Third Party'
    }
  ]
  searchVal = ''

  thirdParties: ThirdParty[] = []

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME'
  }

  renaming = false
  nullThirdPartyName = false
  renameThirdPartyName = ''

  tableColumn = [
    { field: 'name', title: 'Third Parties Name', sortable: true, slots: { default: 'name_default' } },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 112 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'id',
    sortDesc: true
  }

  mounted () {
    this.getThirdParties()
  }

  async getThirdParties () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    const asc = sortDesc || false
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getThirdParties,
        variables: {
          filter: {
            name: searchVal
          },
          order: {
            orderBy: orderBy,
            asc: !asc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.thirdParties = result.data.getThirdParties.thirdParties
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getThirdParties.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  renameThirdParty (e: Event, item: any) {
    e.preventDefault()
    e.stopPropagation()
    this.targetThirdParty = Object.assign({}, item)
    this.renameThirdPartyName = item.name
    this.renaming = true
  }

  stopPreventDefault (e: Event) {
    e.preventDefault()
    e.stopPropagation()
  }

  async renameConfirm (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.nullThirdPartyName = !this.renameThirdPartyName
    if (this.nullThirdPartyName) {
      return
    }
    await this.renameThirdPartyNameConfirm()
    this.renaming = false
  }

  async renameCancel (e: Event) {
    e.preventDefault()
    e.stopPropagation()
    this.renaming = false
    this.renameThirdPartyName = ''
  }

  async renameThirdPartyNameConfirm () {
    if (!this.targetThirdParty) {
      return
    }
    const { id } = this.targetThirdParty
    try {
      await this.$apollo.mutate({
        mutation: editThirdParty,
        variables: {
          thirdPartyId: Number(id),
          thirdPartyInput: {
            name: this.renameThirdPartyName
          }
        }
      })
      showNotification({
        msg: 'Update the third parties name successfully',
        type: SnackType.Success
      })
      this.getThirdParties()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  refreshList () {
    this.searchVal = ''
    this.getThirdParties()
  }

  async deleteThirdPartyConfirm () {
    await this.deleteThirdPartyPost()
    this.getThirdParties()
  }

  routeToDetail (item: any) {
    return {
      name: 'ThirdPartyDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getThirdParties()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getThirdParties()
  }
}
